import React from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, formatDate } from '../regureStyles';
import useRcProRateTablesFunctions from './useRcProRateTablesFunctions';

const rateInputStyle = {
  width: 88,
  padding: '5px 8px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 6,
  fontSize: 12,
  color: S.textPrimary,
  outline: 'none',
  textAlign: 'right',
  boxSizing: 'border-box',
  fontVariantNumeric: 'tabular-nums',
};

// Post-2026-07 AROMED tariff has 2 rate columns keyed by sum insured only —
// franchise is no longer a price dimension.
const RATE_COLUMNS = [
  { key: 'sa5m',  label: 'SA 5M' },
  { key: 'sa10m', label: 'SA 10M' },
];

const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.6 };

const RcProRatesEditor = () => {
  const { t } = useTranslation();
  const {
    table,
    specializations,
    constants,
    updateRate, updateConstant,
    loading, saving, dirty, error, toast,
    save, reload,
  } = useRcProRateTablesFunctions();

  const stampDutyDisplay = constants?.stampDutyRate != null && constants.stampDutyRate <= 1
    ? constants.stampDutyRate * 100
    : constants?.stampDutyRate;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          {table?.effectiveDate && (
            <div style={{ fontSize: 11, color: S.textMuted }}>
              {t('rateTables.effective', { date: formatDate(table.effectiveDate) })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 12, color: S.warning, fontWeight: 600 }}>{t('rateTables.unsavedChanges')}</span>}
          <button
            onClick={reload}
            disabled={loading || saving}
            style={{
              padding: '8px 14px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8,
              fontSize: 13, fontWeight: 600, color: S.textSecondary, cursor: 'pointer',
              opacity: (loading || saving) ? 0.6 : 1,
            }}
          >
            {t('rateTables.reset')}
          </button>
          <BtnPrimary onClick={save} disabled={!dirty || saving} style={{ opacity: (!dirty || saving) ? 0.6 : 1 }}>
            <Icon name='check' color='#fff' size={15} /> {saving ? t('rateTables.saving') : t('rateTables.saveChanges')}
          </BtnPrimary>
        </div>
      </div>

      {table?.isProvisional && (
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: `1px solid ${S.warning}55`, borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 14 }}>
          <Icon name='alert-triangle' color={S.warning} size={13} />{' '}
          <strong>{t('rateTables.provisionalTitle', { defaultValue: 'PROVISIONAL rates' })}</strong> —{' '}
          {t('rateTables.provisionalNote', { defaultValue: "pending Lloyd's underwriter approval." })}
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 14 }}>
          {error}
        </div>
      )}
      {toast && (
        <div style={{ padding: '10px 14px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, fontSize: 13, color: '#166534', marginBottom: 14 }}>
          {toast}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('rateTables.loadingRcPro')}</div>
      ) : (
        <>
          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: S.bgCard }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}`, position: 'sticky', left: 0, background: S.bgCard }}>
                    {t('rateTables.specialization')}
                  </th>
                  <th style={{ padding: '10px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}` }}>
                    {t('rateTables.nogaPlus')}
                  </th>
                  {RATE_COLUMNS.map(c => (
                    <th key={c.key} style={{ padding: '10px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: `1px solid ${S.borderLight}`, whiteSpace: 'nowrap' }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {specializations.map((spec, i) => (
                  <tr key={spec.code} style={{ borderBottom: i < specializations.length - 1 ? `1px solid ${S.borderLight}` : 'none' }}>
                    <td style={{ padding: '6px 14px', fontSize: 12, color: S.textPrimary, fontWeight: 500 }}>
                      {spec.label}
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: S.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                      {spec.nogaCode}
                    </td>
                    {RATE_COLUMNS.map(c => (
                      <td key={c.key} style={{ padding: '4px 8px', textAlign: 'right' }}>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          value={spec.rates?.[c.key] ?? ''}
                          onChange={e => updateRate(i, c.key, e.target.value)}
                          style={rateInputStyle}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: S.textPrimary, marginBottom: 12 }}>{t('rateTables.productConstants')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
              <div>
                <label style={labelStyle}>{t('rateTables.stampDutyLabel')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <input
                    type='number'
                    step='0.1'
                    min='0'
                    max='100'
                    value={stampDutyDisplay ?? ''}
                    onChange={e => updateConstant('stampDutyRate', e.target.value === '' ? '' : Number(e.target.value) / 100)}
                    style={{ ...rateInputStyle, width: 130, textAlign: 'left' }}
                  />
                  <span style={{ fontSize: 12, color: S.textMuted }}>%</span>
                </div>
              </div>
              <div>
                <label style={labelStyle}>{t('rateTables.coverageBasis')}</label>
                <div style={{ marginTop: 6, fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                  {constants?.coverageBasis || 'claims_made'}
                </div>
              </div>
              <div>
                <label style={labelStyle}>{t('rateTables.currency')}</label>
                <div style={{ marginTop: 6, fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                  {constants?.currency || 'CHF'}
                </div>
              </div>
              <div>
                <label style={labelStyle}>{t('rateTables.availableSums')}</label>
                <div style={{ marginTop: 6, fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                  {(table?.availableSumsInsured || []).map(v => `CHF ${v.toLocaleString('de-CH')}`).join(' / ')}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: S.textMuted }}>
              {t('rateTables.rcProExtensibilityNote')}
            </div>
          </div>

          <div style={{ marginTop: 14, fontSize: 11, color: S.textMuted }}>
            {t('rateTables.rateChangesNote')}
          </div>
        </>
      )}
    </>
  );
};

export default RcProRatesEditor;
